import { forwardRef } from 'react';
import styled from 'styled-components';
import {
  Box,
  IconButton2,
  ListItemContextMenu,
  MenuButton,
  MenuGroup,
  noop,
  Spinner,
  Text,
  theme,
  Tooltip,
  useIsListBoxItemSelected,
  whenFocused,
  whenHovered,
} from '@frameio/vapor';
import {
  IconCheckmark,
  IconEdit,
  IconMoreHorizontal,
  IconTrash,
} from '@frameio/vapor-icons';
import TEXT from './generated/TEXT/WatermarkTemplateListItem';

TEXT.DEFINE({
  /** Label for editing template */
  editTemplate: 'Edit Template',
  /** Label for deleting template */
  delete: 'Delete',
  /** Loading state message */
  loadingTemplates: 'Loading Watermark Templates',
  /** Message when template is deleted */
  templateDeleted: 'Template deleted by account admin',
  /** Message when editing is disabled due to plan */
  disableEdit: "Your current plan doesn't allow editing this template",
  /** Message when editing deleted template is disabled */
  disableEditDeleted: "You can't edit deleted templates",
  /** Message when deleting is disabled due to template being in use */
  disableDelete:
    'This template cannot be deleted because it is currently in use.',
});

const TOOLTIP_MAX_WIDTH = '177px';

type WatermarkTemplateListItemProps = {
  item: {
    id: string;
    name: string;
    templateDescription: string;
    deletedAt: string | null;
    inUse: boolean;
  };
  isReadOnly?: boolean;
  canManage?: boolean;
  isDeleteDisabled?: boolean;
  onEdit?: (item: { id: string; name: string }) => void;
  onDelete?: (item: { id: string; name: string }) => void;
};

export const WatermarkTemplateListItem = forwardRef(
  (
    {
      item,
      isReadOnly = false,
      canManage = true,
      isDeleteDisabled = false,
      onEdit = noop,
      onDelete = noop,
    }: WatermarkTemplateListItemProps,
    forwardedRef: React.Ref<HTMLDivElement>
  ) => {
    const isSelected = useIsListBoxItemSelected();

    const handleEdit = () => {
      onEdit?.(item);
    };

    const handleDelete = () => {
      onDelete?.(item);
    };

    return (
      <StyledContent
        ref={forwardedRef}
        data-optimistic={item.id.includes('optimistic')}
        data-selected={isSelected}
        data-testid="watermark-template-list-item"
      >
        <Box position="absolute" right="2px" top="0px">
          {isReadOnly && (
            <Tooltip
              maxWidth={TOOLTIP_MAX_WIDTH}
              placement="top"
              title={
                item.deletedAt ? TEXT.disableEditDeleted : TEXT.disableEdit
              }
            >
              <div>
                <StyledIconButton
                  icon={<IconMoreHorizontal />}
                  isDisabled={true}
                  label={TEXT.disableEdit}
                  size="small"
                  variant="tertiary-dimmed"
                />
              </div>
            </Tooltip>
          )}
          {!isReadOnly && canManage && (
            <ListItemContextMenu
              content={
                <>
                  <MenuGroup>
                    <MenuButton
                      data-testid="edit-template-button"
                      iconBefore={<IconEdit />}
                      onPress={handleEdit}
                    >
                      {TEXT.editTemplate}
                    </MenuButton>
                  </MenuGroup>

                  <MenuGroup>
                    <Tooltip
                      shouldRender={isDeleteDisabled}
                      title={TEXT.disableDelete}
                    >
                      <span>
                        <MenuButton
                          data-testid="delete-template-button"
                          disabled={isDeleteDisabled}
                          iconBefore={<IconTrash />}
                          onPress={handleDelete}
                          variant="destructive"
                        >
                          {TEXT.delete}
                        </MenuButton>
                      </span>
                    </Tooltip>
                  </MenuGroup>
                </>
              }
              minWidth="225px"
              shouldAnimate={false}
              variant="default"
            />
          )}
        </Box>

        <Box
          alignItems="center"
          display="flex"
          style={{ gap: theme.spaces[12] }}
        >
          <Box
            alignItems="center"
            display="flex"
            height="100%"
            justifyContent="center"
            minWidth="24px"
            width={24}
          >
            {isSelected && <IconCheckmark color={theme.colors.brand.primary} />}
            {item.id.includes('optimistic') && (
              <Spinner
                aria-label={TEXT.loadingTemplates}
                isLoading={true}
                size="x-small"
              />
            )}
          </Box>
          <Box
            alignItems="flex-start"
            display="flex"
            flexDirection="column"
            minWidth={0}
            style={{ gap: theme.spaces[4] }}
          >
            <Text
              color="primary"
              data-testid="template-name"
              style={{ width: '100%' }}
              truncate={true}
              variant="link"
            >
              {item.name}
            </Text>
            <Text
              color="tertiary"
              style={{ width: '100%' }}
              truncate={true}
              variant="secondary"
            >
              {item.templateDescription}
            </Text>
            {item.deletedAt && (
              <Text color="error" fontWeight="medium" variant="secondary">
                {TEXT.templateDeleted}
              </Text>
            )}
          </Box>
        </Box>
      </StyledContent>
    );
  }
);

const StyledIconButton = styled(IconButton2)``;

const StyledContent = styled.div`
  gap: ${theme.spaces[12]};
  grid-template-columns: auto 1fr;
  padding: ${theme.spaces[2]} ${theme.spaces[48]} ${theme.spaces[2]}
    ${theme.spaces[4]};
  position: relative;
  width: 100%;

  & ${StyledIconButton} {
    visibility: hidden;
  }

  ${whenFocused},
  ${whenHovered} {
    ${StyledIconButton} {
      visibility: visible;
    }
  }
`;
